import moment from 'moment';
import { LocaleService } from 'app/services/locale/locale.service';
import React, {ComponentType, useState} from "react";
import StandbyConfirmationMsg from "external-components/standby-confirmation-message";
import {IProps, IContainerProps, IServices, IHandlers, classPrefix} from 'external-components/standby-confirmation-message/types'

/**
 * Gets the interface types as array of strings so we don't need hand code them each time (and reduces chance for mistakes).
 */
import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import {ThemeProvider} from "@material-ui/core/styles";
import ReactUtilsService from "app/services/reactUtils.service";
import {StandbyListService as LegacyStandbyListService} from "app/services/standbyList/StandbyList.service";
import EditBookingActions, {IConfirmStandbyReponse} from "app/actions/editBooking.actions";
import ServicePicker from "app/bookings/edit/ServicePicker";
import DateUtilsService from 'external-services/date-utils-service';
import store from 'appRoot/Store';
import { rootDisplayActions } from 'app/reducers/display/slice';
import { ToastType } from 'shared-types/SharedTypes';

const NS = 'StandbyConfirmationMsgContainer';


const propsKeys: string[] = keys<IProps>();
const containerPropsKeys: string[] = keys<IContainerProps>();
const handlersKeys: string[] = keys<IHandlers>();
const servicesKeys: string[] = keys<IServices>();

const ThemeWrap = function(all: IProps & IContainerProps & IHandlers & IServices) {

  /**
   * Sorts 'all' into IServices, IProps and IContainerProps
   */
  const {EditBookingActions}: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices;
  const props: IProps = ReactUtilsService.getObjectByKeys(all, propsKeys) as IProps;
  const {
    booking, selectedTime, isAllowedToSend, onStandbyUpdate, onStandbyReset, standbyConfirmationExpiry
  }: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>(null);

  props.errorMsg = errorMsg;
  props.isLoading = isLoading;
  // time kept as-is, date portion localised
  const selectedDate = moment(selectedTime.time);
  props.formattedTime = selectedDate.format('h:mma') + ' ' + LocaleService.formatDate(selectedDate.toDate(), 'full');
  props.bookingExpired = DateUtilsService.checkIfStandbyConfirmationExpired(standbyConfirmationExpiry);
  // expiry: convert from UTC to local before formatting
  const expiryLocal = standbyConfirmationExpiry ? moment.utc(standbyConfirmationExpiry).local() : null;
  props.standbyConfirmationExpiry = expiryLocal
    ? expiryLocal.format('h:mma') + ' ' + LocaleService.formatDate(expiryLocal.toDate(), 'full')
    : null;

  /**
   * Handlers used to modify app state (via a service) from within the module's container.
   * Similar to redux mapDispatchToProps.
   */
  const handlers: IHandlers = {
    handleResendClick: () => {
      if (!isAllowedToSend) {
        onStandbyUpdate(false, null, true);
        return;
      }
      onStandbyUpdate(true, null);
      setErrorMsg(null);
      setIsLoading(true);
      EditBookingActions.confirmStandby(booking)
        .subscribe(({status, booking}: IConfirmStandbyReponse) => {
          setIsLoading(false);

          switch (status) {
            case 200:
              onStandbyUpdate(false, booking);
              const fullName = [booking.firstName, booking.lastName].filter(Boolean).join(' ');
              store.dispatch(
                rootDisplayActions.showNewToast({
                  msg: `Standby request was successfully resent for ${fullName}.`,
                  type: ToastType.info,
                })
              );
              setTimeout(() => {
                store.dispatch(rootDisplayActions.clearToast());
              }, 5000);
              break;

            case 408:
              setErrorMsg('Sorry, server took too long to respond (timeout). Try closing and reopening the booking.');
              onStandbyUpdate(false, null);
              break;

            default:
              console.warn(NS, 'confirmStandby failed', status);
              setErrorMsg(LegacyStandbyListService.getGenericErrorResponseMessaging(status));
              onStandbyUpdate(false, null);
          }
        }, (err: string) => {
          console.warn(NS, 'confirmStandby failed', err);
          setIsLoading(false);
          onStandbyUpdate(false, null);
          setErrorMsg('Sorry, there seems to have been a problem with your internet connection.');
        });
    },
    handleResetClick: () => {
      onStandbyReset();
    }
  }

  return ReactUtilsService.containerWrap(
    <StandbyConfirmationMsg {...props} {...handlers} />, classPrefix
  );
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2AStandbyConfirmationMsg: [ComponentType<unknown>, never[]?, string[]?] = [
  hot(module)(ThemeWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[],
  servicesKeys
];
